import React, { Component } from 'react'
import ReactAutosuggest from 'react-autosuggest'
import match from 'autosuggest-highlight/match'
import parse from 'autosuggest-highlight/parse'
import TextField from '@material-ui/core/TextField'
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper'
import LinearProgress from '@material-ui/core/LinearProgress';
import StyleSheet from 'styles'
import {debounce, eventKeyMatches, KEYS} from 'utils'
import Chip from '@material-ui/core/Chip'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from '@material-ui/icons/Close'

export class MultiAutoSuggest extends Component {
  static defaultProps = {
    inputProps:                  {},
    onSuggestionsFetchRequested: () => {},
    onChange:                    () => {},
    labelProvider:         ({name}) => name || '',
    valueProvider:            value => [value],
    values:                      []
  }

  constructor(props) {
    super(props)
    this.state = {
      value: '',
      suggestions: [],
      loading: 0
    }
  }

  handleChange = (event, { newValue: value }) =>
    this.setState({value})

  handleFocus = () => {
    this._focus = true
  }

  handleBlur = event => {
    this._focus = false
    this.setState({value: '', suggestions: []})
  }

  handleSuggestionSelected = (event, {suggestion}) => {
    const values = this.props.values.concat(this.props.valueProvider(suggestion))
    const nextSuggestions = this.state.suggestions.filter(s => s !== suggestion)
    this.setState({
      value: this.state.value,
      suggestions: nextSuggestions
    })
    this.props.onChange({target: { value: values }})
  }

  handleRequestDelete = (toDelete) => event => {
    const values = this.props.values.filter(suggestion => suggestion !== toDelete)
    this.props.onChange({target: { value: values }})
    this.setState(
      this._focus ? this.onSuggestionsFetchRequested : null
    )
  }

  handleClearValues = event => {
    this.props.onChange({target: { value: [] }})
    event.preventDefault()
  }

  finishLoad = (loadCounter=this.state.loading - 1) =>
    setTimeout(() => this.setState({loading: Math.max(0, loadCounter)}), 100)

  preventDefault = event =>
    event.preventDefault()

  onSuggestionsFetchRequested = debounce(() => {
    this.props.onSuggestionsFetchRequested(this.state.value, (newValues) => {
        this.finishLoad()
        this.setState({
          suggestions: newValues
        })
      },
      this.finishLoad
    )
    this.setState({loading: this.state.loading + 1})
  }, 300)

  onSuggestionsClearRequested = () => {
    this.setState({suggestions: []})
  }

  getSectionSuggestions = ({suggestions, className}) =>
    suggestions.map(s => ({...s, className}))

  renderSuggestion = (suggestion, { query, isHighlighted })  => {
    const label = this.props.labelProvider(suggestion)
    const matches = match(label, query)
    const parts   = parse(label, matches)
    //TODO: remove reference to countries and add className to the sections
    const isCountry = suggestion.type === 'countries'
    return (
      <MenuItem className={styles({countryItem: isCountry, countryGroupItem: !isCountry})} selected={isHighlighted} component="div">
        <div>
          {parts.map((part, index) => {
            return part.highlight ? (
              <span key={index} style={{ fontWeight: 500, color: "#6AAEAA" }}>
                {part.text}
              </span>
            ) : (
              <strong key={index} style={{ fontWeight: 500 }}>
                {part.text}
              </strong>
            )
          })}
        </div>
      </MenuItem>
    )
  }

  renderSuggestionsContainer = ({ containerProps, children }) =>
    <Paper {...containerProps} className={`${containerProps.className} ${styles.suggestionsContainer}`}>
      {children}
      {this.state.loading ? <LinearProgress /> : false}
    </Paper>

  renderSectionTitle = ({title}) =>
    <Typography className={styles.sectionTitle} variant='subtitle1'>{title}</Typography>

  renderInput = ({ref, ...props}) => {
    return (
      <div>
        <TextField  inputRef={inputRef =>{
          this._inputRef = inputRef
          ref(inputRef)
        }} {...props} className={`${props.className} ${styles.autoSuggestInput}`}/>
        <IconButton onMouseDown={this.preventDefault} onClick={this.handleClearValues} color="primary" aria-label="Clear values">
          <CloseIcon/>
        </IconButton>
        <div className={styles.chips}>
          {this.props.values.map((suggestion, idx) =>
            <Chip
              label={this.props.labelProvider(suggestion)}
              key={idx}
              onClick={this.preventDefault}
              onMouseDown={this.preventDefault}
              onDelete={this.handleRequestDelete(suggestion)}
              className={styles.chip}
            />
          )}
        </div>
      </div>
    )
  }

  render = () => {
    const {className, valueProvider, labelProvider, onSuggestionsFetchRequested, onChange, multiSection,...inputProps } = this.props
    const { onKeyUp } = inputProps
    const onKeyUpWrapper = (event) => {
      if(eventKeyMatches(event, KEYS.enter)) event.stopPropagation()
      onKeyUp && onKeyUp(event)
    }
    return (
      <ReactAutosuggest
        theme={{container: className}}
        renderInputComponent={this.renderInput}
        inputProps={{
          ...inputProps,
          onKeyUp: onKeyUpWrapper,
          value: this.state.value || '',
          onFocus: this.handleFocus,
          onBlur: this.handleBlur,
          onChange: this.handleChange
        }}
        suggestions={this.state.suggestions}
        onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
        onSuggestionsClearRequested={this.onSuggestionsClearRequested}
        onSuggestionSelected={this.handleSuggestionSelected}
        alwaysRenderSuggestions={!!this.state.suggestions.length}
        getSuggestionValue={this.props.labelProvider}
        getSectionSuggestions={this.getSectionSuggestions}
        renderSuggestion={this.renderSuggestion}
        renderSuggestionsContainer={this.renderSuggestionsContainer}
        renderSectionTitle={this.renderSectionTitle}
        multiSection={multiSection}
        containerProps={{className}}
      />
    )
  }
}

const styles = StyleSheet({
  container: {
    flex: 1
  },
  autoSuggestInput: {
    width: 'calc(100% - 50px)'
  },
  chip: {
    float: 'left',
    margin: 2,
    backgroundColor: '#A59DAF',
    '&:hover': {
      backgroundColor: '#CBBFDA'
    }
  },
  chips: {
    display: 'flex',
    flex: 1,
    flexWrap: 'wrap'
  },
  suggestionsContainer: {
    '& ul': {
      padding: 0,
      listStyle: 'none',
      backgroundColor: '#20173A'
    }
  },
  countryItem: {
    height: 10
  },
  countryGroupItem: {
    height: 10,
    color: 'white',
    background: 'rgba(80, 80, 80, 0.5)'
  },
  sectionTitle: {
    display: 'none',
    color: "rgba(255, 255, 255, 1)",
    fontSize: 16,
    background: "rgba(93, 93, 93, 0.7)",
    textAlign: "center",
    borderBottom: "3px solid rgba(232, 197, 109, 0.45)",
    marginBottom: -20
  }
})

export default MultiAutoSuggest